import { overlay } from "overlay-kit";
import { type FC } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import type { OEmbedType } from "../../../../shared/types/oembed";
import type { OEmbedDataType } from "../../../../shared/types/worksheet";

import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { fetchOEmbed } from "utils/oembed";

type OEmbedModalProps = {
  onClose?: () => void;
  onConfirm: (oEmbed: OEmbedDataType) => void;
  showEmbedPageLabel?: boolean;
  url?: string;
};

type OEmbedFormValues = {
  url: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showOEmbedModal = (url: string, showEmbedPageLabel = false) => {
  return overlay.openAsync<OEmbedDataType | false>(({ isOpen, close }) => (
    <Dialog open={isOpen} title={t("fields.embed.title")} onClose={() => close(false)}>
      <OEmbedModal
        showEmbedPageLabel={showEmbedPageLabel}
        url={url}
        onClose={() => close(false)}
        onConfirm={(data) => close(data)}
      />
    </Dialog>
  ));
};

export const OEmbedModal: FC<OEmbedModalProps> = ({
  onClose,
  onConfirm,
  url: initialUrl = "",
  showEmbedPageLabel = false,
}) => {
  const form = useForm<OEmbedFormValues>({
    defaultValues: { url: initialUrl },
  });

  const labelKey = showEmbedPageLabel ? "fields.embed.embed_page_label" : "fields.embed.label";

  const onSubmit: SubmitHandler<OEmbedFormValues> = async ({ url: submittedUrl }) => {
    let oEmbedData: OEmbedType | undefined;
    try {
      const { embed, errorCode } = await fetchOEmbed(submittedUrl);

      if (errorCode === "401") {
        form.setError("url", {
          type: "manual",
          message: t("fields.embed.unauthorized"),
        });
        return;
      }

      // Any other error code will be treated as an invalid URL and oEmbedData will be undefined
      oEmbedData = embed;
    } catch (e) {
      console.error(e);
    }

    if (oEmbedData) {
      // This will close the modal
      onConfirm({ url: submittedUrl, o: oEmbedData });
      return;
    }

    form.setError("url", {
      type: "manual",
      message: t("fields.embed.invalid_url"),
    });
  };

  return (
    <FormProvider {...form}>
      <form className="body-s text-black-tint-10" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField label={t(labelKey)} name="url" required>
          {(controlProps) => (
            <TextField
              {...form.register("url", {
                required: t("error.field_required"),
                validate: (value) => value.trim().length > 0 || t("error.field_required"),
                onChange: () => form.clearErrors("url"),
              })}
              {...controlProps}
              placeholder="https://www.youtube.com/"
            />
          )}
        </FormField>

        <div className="mt-8 flex w-full items-center justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton kind="accent" size="m" title={t("fields.embed.embed_video")} type="submit">
            {t("fields.embed.embed_video")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
